import { APP_NAME, OPERATOR_DISCLAIMER, PRIVACY_CONTACT_EMAIL } from '@foosball/shared';
import { Link } from 'react-router';

import { Card } from '../components/ui';

/**
 * Privacy notice.
 *
 * Deliberately reachable without signing in — people need to read it *before*
 * handing over an email address, and UK GDPR Art 13 wants the information given
 * at the point of collection, not behind the thing it is collected for.
 *
 * Every claim here must stay true of the code. If a retention period or a right
 * described below stops matching what the app actually does, this page becomes
 * a misstatement rather than a nicety.
 */

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-2">
      <h2 className="text-base font-semibold">{title}</h2>
      <div className="space-y-2 text-sm leading-relaxed text-slate-600 dark:text-slate-300">
        {children}
      </div>
    </section>
  );
}

export function PrivacyPage() {
  return (
    <main className="mx-auto max-w-2xl px-4 py-10">
      <h1 className="mb-2 text-2xl font-bold tracking-tight">Privacy notice</h1>
      <p className="mb-6 text-sm text-slate-500 dark:text-slate-400">
        How {APP_NAME} handles your personal data.
      </p>

      <div className="mb-6 rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm leading-relaxed text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
        <strong className="block">Who runs this</strong>
        {OPERATOR_DISCLAIMER} If you have a question or a complaint about your data, it comes
        to the person running the league — not to OXTS.
      </div>

      <Card className="space-y-6">
        <Section title="Who is responsible for your data">
          <p>
            The league is run privately by a colleague, who is the data controller for it. Contact{' '}
            <a
              href={`mailto:${PRIVACY_CONTACT_EMAIL}`}
              className="font-medium text-emerald-700 hover:underline dark:text-emerald-400"
            >
              {PRIVACY_CONTACT_EMAIL}
            </a>{' '}
            about anything on this page.
          </p>
        </Section>

        <Section title="What is collected">
          <ul className="list-disc space-y-1 pl-5">
            <li>
              <strong>Your email address</strong> — to sign you in, to reset your password, and to
              tell you when someone challenges you.
            </li>
            <li>
              <strong>Your display name</strong> — shown to everyone else in the league, on the
              table, on match records and on your profile.
            </li>
            <li>
              <strong>Your match results and rating history</strong> — the purpose of the app.
              These are visible to every signed-in player.
            </li>
            <li>
              <strong>Your password</strong> — never stored. Only an argon2id hash of it is kept,
              which cannot be turned back into your password.
            </li>
            <li>
              <strong>Technical details of your sign-ins</strong> — your browser's user-agent
              string, and a salted one-way hash of your IP address (not the address itself), used
              to spot suspicious activity.
            </li>
            <li>
              <strong>Web server logs</strong> — the server records requests, including full IP
              addresses, in the ordinary way. These rotate and are deleted automatically.
            </li>
          </ul>
        </Section>

        <Section title="Why, and on what basis">
          <p>
            The lawful basis is <strong>legitimate interests</strong>: running a social football
            league that you asked to join, and keeping the accounts in it secure. There is no
            profiling, no advertising, and no automated decision-making about you.
          </p>
          <p>
            Taking part is entirely voluntary, and nothing here affects your employment in any way.
          </p>
        </Section>

        <Section title="Who else sees it">
          <ul className="list-disc space-y-1 pl-5">
            <li>
              <strong>Other players</strong> see your display name, results, ratings and
              achievements. They do <strong>not</strong> see your email address.
            </li>
            <li>
              <strong>Google</strong> — outgoing email is sent through a Gmail account, so your
              email address passes through Google's systems in order to reach you.
            </li>
          </ul>
          <p>
            Nothing is sold, shared for marketing, or passed to anyone else. The site loads no
            third-party scripts, fonts or trackers whatsoever.
          </p>
        </Section>

        <Section title="Cookies">
          <p>
            One cookie is used: <code className="rounded bg-slate-100 px-1 dark:bg-slate-800">foosball_refresh</code>,
            which keeps you signed in. It is strictly necessary for a service you asked for, so no
            consent banner is required for it. There are no analytics or advertising cookies, and
            nothing is stored in your browser beyond that one cookie.
          </p>
        </Section>

        <Section title="How long it is kept">
          <p>
            Your account and match history are kept for as long as the league runs, because
            removing old results would break the league table and everyone else's ratings. Expired
            sign-in sessions and password-reset tokens are cleared out automatically. Server logs
            rotate on the usual schedule and are then deleted.
          </p>
        </Section>

        <Section title="Your rights">
          <p>
            You can ask to see the data held about you, to correct it, to have it deleted, or to
            object to it being held at all. Email{' '}
            <a
              href={`mailto:${PRIVACY_CONTACT_EMAIL}`}
              className="font-medium text-emerald-700 hover:underline dark:text-emerald-400"
            >
              {PRIVACY_CONTACT_EMAIL}
            </a>{' '}
            and it will be handled within one month. Requests are dealt with by hand — this is a
            small league, not a company.
          </p>
          <p>
            On deletion: your email address and display name are removed, and your past matches are
            reassigned to a former player, so that other people's results and ratings stay correct.
            If you would rather everything went, say so and it will be discussed with you.
          </p>
          <p>
            You can change your display name yourself at any time from your profile.
          </p>
        </Section>

        <Section title="Security">
          <p>
            The site is served over HTTPS. Passwords are hashed with argon2id, sign-in tokens are
            stored only as hashes and are revoked wholesale if one is ever reused, and IP addresses
            are salted and hashed rather than stored. The server is a self-hosted machine, not a
            cloud service.
          </p>
        </Section>

        <Section title="Complaints">
          <p>
            If you are unhappy with how your data has been handled, please raise it directly first.
            You also have the right to complain to the Information Commissioner's Office at{' '}
            <span className="font-medium">ico.org.uk</span>.
          </p>
        </Section>
      </Card>

      <p className="mt-6 text-center text-sm text-slate-500 dark:text-slate-400">
        <Link
          to="/login"
          className="font-medium text-emerald-700 hover:underline dark:text-emerald-400"
        >
          Back to sign in
        </Link>
      </p>
    </main>
  );
}
